import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, pickErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url.replace(/^\/api/, "")}`;
}

export default function StudentDashboard() {
  const { student, logout } = useAuth();
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyCategory, setBusyCategory] = useState(null);
  const [actionError, setActionError] = useState("");

  const categoryNameById = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [categories]);

  const load = useCallback(async () => {
    if (!student?.student_id) return;
    setLoading(true);
    setError("");
    try {
      const [testsRes, catsRes] = await Promise.all([
        api.get(`/student/${student.student_id}/tests`),
        api.get("/categories"),
      ]);
      setTests(Array.isArray(testsRes.data) ? testsRes.data : []);
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
    } catch (err) {
      setError(pickErr(err, "Failed to load your tests"));
    } finally {
      setLoading(false);
    }
  }, [student?.student_id]);

  useEffect(() => {
    load();
  }, [load]);

  function onLogout() {
    logout();
    navigate("/student/login", { replace: true });
  }

  function onStart(categoryId) {
    navigate(`/student/test/${categoryId}`);
  }

  async function onViewReport(categoryId) {
    setActionError("");
    setBusyCategory(categoryId);
    try {
      const { data } = await api.post("/report", {
        student_id: student.student_id,
        category_id: categoryId,
        student_dob: student.dob,
      });
      window.open(toAbsolute(data.pdf_url), "_blank", "noopener,noreferrer");
    } catch (err) {
      setActionError(pickErr(err, "Failed to load report"));
    } finally {
      setBusyCategory(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
          <div>
            <h1 className="text-base sm:text-lg font-semibold text-slate-900">
              Student Panel
            </h1>
            <p
              className="text-xs text-slate-500"
              data-testid="student-roll-label"
            >
              Code {student?.student_code || student?.roll_number}
            </p>
          </div>
          <button
            onClick={onLogout}
            data-testid="student-logout"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">
          Your assessments
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Tests assigned to you by your school.
        </p>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : error ? (
          <p
            data-testid="student-tests-load-error"
            className="text-sm text-red-600"
          >
            {error}
          </p>
        ) : tests.length === 0 ? (
          <p
            data-testid="student-no-tests"
            className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600"
          >
            You have no tests assigned yet. Please check back later.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="student-tests-list">
            {tests.map((t) => {
              const completed = t.status === "completed";
              const inProgress = t.status === "started";
              return (
                <li
                  key={t.category_id}
                  className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  data-testid={`student-test-row-${t.category_id}`}
                >
                  <div>
                    <div className="font-medium text-slate-900">
                      {categoryNameById[t.category_id] || "Test"}
                    </div>
                    <div className="text-xs mt-1">
                      <span
                        className={
                          "inline-block px-2 py-0.5 rounded-full " +
                          (completed
                            ? "bg-emerald-50 text-emerald-700"
                            : inProgress
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-700")
                        }
                      >
                        {t.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {completed ? (
                      <button
                        onClick={() => onViewReport(t.category_id)}
                        disabled={busyCategory === t.category_id}
                        data-testid={`student-view-report-${t.category_id}`}
                        className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
                      >
                        {busyCategory === t.category_id
                          ? "Opening…"
                          : "View Report"}
                      </button>
                    ) : (
                      <button
                        onClick={() => onStart(t.category_id)}
                        data-testid={`student-start-${t.category_id}`}
                        className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800"
                      >
                        {inProgress ? "Continue Test" : "Start Test"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {actionError ? (
          <p
            data-testid="student-action-error"
            className="mt-4 text-sm text-red-600"
          >
            {actionError}
          </p>
        ) : null}
      </main>
    </div>
  );
}
